import { Component, OnDestroy, Input } from '@angular/core';
import { Router } from '@angular/router';

import { AirValidationResponseError, AirValidationResultType } from '@app/entities/air-validation.response';
import { BookingFacade } from '@booking/booking.facade';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { LocalStorageService } from '@app/services/local-storage.service';
import { STEP_SECTION } from '@packages/packages-result/constants/packages-result.routes';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { packagesResultRootRoute } from '@packages/packages-result/constants/packages-result.routes';
import { AppFacade } from '@app/app.facade';
import { ButtonSize } from '@design-system/components/molecules/button/entities/button.types';

@Component({
  selector: 'app-booking-alert',
  templateUrl: './booking-alert.component.html',
  styleUrls: ['./booking-alert.component.sass'],
  standalone: false
})
export class BookingAlertComponent implements OnDestroy {

  @Input() validationError: AirValidationResponseError;
  @Input() hotelValidation: HotelValidationResponse;
  @Input() travelType: TravelBookingType;
  @Input() isMobile: boolean;

  private readonly activeErrorCodeOptions = [
    AirValidationResultType.NotAvailable,
    AirValidationResultType.ErrorServer,
    AirValidationResultType.InvalidToken,
    AirValidationResultType.AirValidateFailed,
    AirValidationResultType.ValidatePackageGetSearchHotel,
    AirValidationResultType.InvalidHotelOptionId,
    AirValidationResultType.hotelvalidate
  ];

  public readonly buttonSize = ButtonSize.SM;

  constructor(
    private readonly router: Router,
    private readonly facade: BookingFacade,
    private readonly localStorageService: LocalStorageService,
    private readonly appFacade: AppFacade,
    private readonly headerService: HeaderService,
  ) { }

  ngOnDestroy(): void {
    this.facade.resetAllBookValidationError();
  }

  get active(): boolean {
    return !!this.validationError?.Code && this.activeErrorCodeOptions.includes(this.validationError?.Code);
  }

  get isInvalidTokenError(): boolean {
    return this.validationError?.Code === AirValidationResultType.InvalidToken;
  }

  get alertText(): string {
    return this.isInvalidTokenError ? 
      'PACKAGES_MODULES.PACKAGES_RESULT_MODULE.NO_RESULTS.ERROR' : 
      'BOOKING_MODULE.TEXT_ALERT_OPTION_NOT_AVAILABLE';
  }

  get alertButtonText(): string {
    return this.isInvalidTokenError ? 
      'PACKAGES_MODULES.PACKAGES_RESULT_MODULE.NO_RESULTS.RESEARCH' : 
      'BOOKING_MODULE.ACCEPT';
  }

  private get isPackageHotelError(): boolean {
    return !!this.validationError?.Code &&
      (this.validationError?.Code === AirValidationResultType.ValidatePackageGetSearchHotel ||
      this.validationError?.Code === AirValidationResultType.NotAvailable ||
      this.validationError?.Code === AirValidationResultType.InvalidHotelOptionId ||
      this.validationError?.Code === AirValidationResultType.hotelvalidate);
  }

  public onAccept(): void {
    switch (this.travelType) {
      case TravelBookingType.PACKAGE_AIR:
        if (this.isPackageHotelError) {
          const decodeUrl = this.localStorageService.getDecodePackageBaseUrlResult();
          this.headerService.forceNavigateByUrl(
            decodeUrl.replace(STEP_SECTION, PackageStep.HOTEL_RESULTS), packagesResultRootRoute);
        } else {
          this.closeAlert();
        }
        break;
      default:
        if (this.isInvalidTokenError) {
          this.reload();
        } else {
          this.closeAlert();
        }
        break;
    }
  }

  private async reload(): Promise<void> {
    const url = this.router.url;
    const navigateByDefault = await this.router.navigateByUrl('/', { skipLocationChange: true });
    if (navigateByDefault) {
      await this.router.navigateByUrl(`/${url}`);
    }
  }

  private closeAlert(): void {
    this.appFacade.historyPop();
    this.appFacade.removeLastNavigationItem();
    this.appFacade.navigateToLastRoute();
  }
}
